import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import InteractiveTable from 'components/InteractiveTable';
import { Typography } from '@mui/material';
import { CurrencyCode } from 'further-types/lp/vehicle';
import EqualisationsConcertina from 'components/EqualisationElements/EqualisationsConcertina';

const EqualisationInterestCalculationsItem: React.FC<{
  action: {
    id: string;
    name: string;
    ledgerDate: CalendarDay;
    totalAmountLcy: number;
    newInvestorBreakdown: Array<{
      investorName: string;
      commitmentId: string;
      investments: number;
      fees: number;
      expenses: number;
      workingCapital: number;
      returnOfCapital: number;
      netCallOrDistribution: number;
      callOverpaidOrUnderpaid: number;
      runningTotalCallOverpaidOrUnderpaid: number;
      equalisationInterestTotalLcy: number;
      equalisationInterestPercentage: number;
    }>;
  };
  baseCurrency: CurrencyCode;
  index: number;
  isLastElement: boolean;
}> = ({ action, index, isLastElement, baseCurrency }) => {
  const columns = [
    {
      field: 'investorName',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'investments',
      headerName: 'Call - Investments',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.investments, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      flex: 1,
    },
    {
      field: 'expenses',
      headerName: 'Call - Expenses',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.expenses, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      flex: 1,
    },
    {
      field: 'returnOfCapital',
      headerName: 'Capital refunds',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.returnOfCapital, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      flex: 1,
    },
    {
      field: 'workingCapital',
      headerName: 'Call - general capital',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.workingCapital, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      flex: 1,
    },
    {
      field: 'netCallOrDistribution',
      headerName: 'Net call/(distribution)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.netCallOrDistribution, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      flex: 1,
    },

    {
      field: 'callOverpaidOrUnderpaid',
      headerName: 'Call underpaid/(overpaid)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.callOverpaidOrUnderpaid, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      flex: 1,
    },
    {
      field: 'equalisationInterestTotalLcy',
      headerName: 'Equalisation interest payable / (receivable)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.equalisationInterestTotalLcy, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      flex: 1,
    },
  ];
  return (
    <EqualisationsConcertina
      key={index}
      gridTemplateColumns="60% 20% 18% 2%"
      isLastElement={isLastElement}
      expandedContent={
        <>
          <InteractiveTable
            id="investor-breakdown-table"
            columns={columns}
            rows={action.newInvestorBreakdown}
          />
        </>
      }
      concertinaItems={[
        <Typography variant="body1" key="capital-action-name">
          {action.name ?? ''}
        </Typography>,
        <Typography variant="body1" key="ledger-date">
          {action?.ledgerDate ? action.ledgerDate.toLocalDateLabel() : '-'}
        </Typography>,
        <Typography variant="body1" key="total-amount">
          {numberToCurrencyString(action.totalAmountLcy, {
            fallback: '-',
            currency: baseCurrency,
            negativeInBrackets: true,
          })}
        </Typography>,
      ]}
    />
  );
};

export default EqualisationInterestCalculationsItem;
